import type { StoryCard } from '@org/model';
import { zineColors, zineFonts } from '../zine/zine-theme';

/**
 * "Ela chegou" story design tokens. The story speaks the same printed-zine
 * language as the class page and the journey wall, so the palette and the
 * font stacks are the zine's; only the few story-only tones live here.
 */

/** Root class that scopes every story rule (see StoriesStyles). */
export const STORIES_ROOT_CLASS = 'z-story';

/** Design size of one story page — everything inside is laid out in these px. */
export const STORY_STAGE_WIDTH = 390;
export const STORY_STAGE_HEIGHT = 844;

/** Full-screen viewer layer: above the app shell and AnnouncementGate (9000), below CelebrationOverlay (9999). */
export const STORY_VIEWER_Z_INDEX = 9500;

export const storyColors = {
  red: zineColors.red,
  sizzle: zineColors.sizzle,
  charm: zineColors.charm,
  baby: zineColors.baby,
  black: zineColors.ink,
  white: '#FFFFFF',
  cream: zineColors.paper,
  /** the disc behind the colour collage portraits */
  portraitDisc: zineColors.cream,
  rose: '#F9E4E0',
  dark: '#2D1A1F',
  mid: zineColors.mid,
  warm: zineColors.warmGray,
  bord: zineColors.line,
  videoBg: '#120B10',
} as const;

export const storyFonts = {
  cond: zineFonts.cond,
  serif: zineFonts.serif,
} as const;

export interface StoryPosterStyle {
  bg: string;
  text: string;
  tape: string;
}

/** The four poster styles of the journey wall (same order as the wall). */
export const STORY_POSTER_STYLES: readonly StoryPosterStyle[] = [
  { bg: storyColors.red, text: storyColors.white, tape: 'rgba(255,255,255,0.15)' },
  { bg: storyColors.baby, text: storyColors.red, tape: 'rgba(212,25,32,0.1)' },
  { bg: storyColors.black, text: storyColors.white, tape: 'rgba(255,255,255,0.15)' },
  { bg: storyColors.charm, text: storyColors.dark, tape: 'rgba(212,25,32,0.1)' },
] as const;

export function storyPosterStyle(index: number | null | undefined): StoryPosterStyle {
  const n = STORY_POSTER_STYLES.length;
  const i = Number.isFinite(index) ? Math.trunc(index as number) : 0;
  return STORY_POSTER_STYLES[((i % n) + n) % n];
}

/** Seal / accent colour of a poster style (the pale-pink poster stamps in red). */
export function storyPosterAccent(style: StoryPosterStyle): string {
  return style.bg === storyColors.baby ? storyColors.red : style.bg;
}

export interface StoryPageTheme {
  bg: string;
  fg: string;
  acc: string;
  dark?: boolean;
  halftone?: boolean;
  /** headline colour (quote card) */
  head?: string;
  /** reveal only: the poster colour that floods the page, and the name colour on it */
  ubg?: string;
  uhead?: string;
  /** reveal only: readable text colours on the flooded poster colour (main / soft) */
  ufg?: string;
  ufgSoft?: string;
}

export type StoryPageKind = 'reveal' | 'closing' | StoryCard['type'];

/** Page colours, exactly as the prototype's `themeOf`. */
export function storyPageTheme(
  page: { kind: 'reveal' } | { kind: 'closing' } | { kind: 'card'; card: StoryCard },
  posterStyleIndex?: number | null,
): StoryPageTheme {
  const us = storyPosterStyle(posterStyleIndex);
  const P = storyColors;
  if (page.kind === 'reveal') {
    // Same rule as the wall posters: red type on the pale poster is kept for
    // headlines only; small text goes dark. White-type posters keep white.
    const onPoster = us.text === P.red ? P.dark : us.text;
    const light = onPoster === P.white;
    return {
      bg: P.rose,
      fg: P.dark,
      acc: us.text,
      ubg: us.bg,
      uhead: us.text,
      ufg: onPoster,
      ufgSoft: light ? 'rgba(255,255,255,0.82)' : P.mid,
    };
  }
  if (page.kind === 'closing') return { bg: P.rose, fg: P.dark, acc: P.red };
  const card = page.card;
  switch (card.type) {
    case 'text':
      if (card.theme === 'red') return { bg: P.red, fg: P.white, acc: P.baby, dark: true, halftone: true };
      if (card.theme === 'paper') return { bg: P.cream, fg: P.dark, acc: P.red, head: P.red };
      return { bg: P.black, fg: P.cream, acc: P.charm, dark: true };
    case 'video':
      return { bg: P.videoBg, fg: P.white, acc: P.red, dark: true };
    case 'notebook':
      return { bg: us.bg === P.baby ? P.charm : P.baby, fg: P.dark, acc: P.red };
    case 'quote':
      return { bg: us.bg, fg: us.text === P.red ? P.dark : us.text, acc: P.red, head: us.text };
    default:
      return { bg: P.black, fg: P.cream, acc: P.charm, dark: true };
  }
}
